import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarDays, Search, X } from "lucide-react";
import {
  PERIOD_OPTIONS,
  MONTHS,
  YEAR_OPTIONS,
  DEFAULT_HISTORY_FILTERS,
  periodLabel,
} from "@/components/HistoryFilters";

/**
 * Barra de filtros do HISTÓRICO — reutilizada por PGA, Salas e Cabines.
 *
 * Os filtros vivem no estado do PAI, portanto continuam preservados
 * enquanto o usuário estiver no módulo. O botão "Limpar filtros" volta
 * para o padrão SEMPRE seguro ("Todo o histórico").
 */
export default function HistoryFiltersBar({
  filters,
  onChange,
  extraSelects = null,
  showDeletedFilter = true,
  testId = "history",
}) {
  const set = (patch) => onChange({ ...filters, ...patch, page: 1 });

  const clear = () => onChange({ ...DEFAULT_HISTORY_FILTERS, page: 1 });

  const isDefault =
    filters.period === DEFAULT_HISTORY_FILTERS.period &&
    !filters.search &&
    filters.hasOccurrence === "todos" &&
    (filters.includeDeleted || "ativos") === "ativos" &&
    !filters.technicianId;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${testId}-filters`}>
      <CalendarDays size={14} className="text-muted-foreground" />

      <Select value={filters.period} onValueChange={(v) => set({ period: v })}>
        <SelectTrigger className="w-44 h-9" data-testid={`${testId}-period`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PERIOD_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {(filters.period === "mes" || filters.period === "ano") && (
        <Select value={filters.year} onValueChange={(v) => set({ year: v })}>
          <SelectTrigger className="w-28 h-9" data-testid={`${testId}-year`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {YEAR_OPTIONS.map((y) => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {filters.period === "mes" && (
        <Select value={filters.month} onValueChange={(v) => set({ month: v })}>
          <SelectTrigger className="w-36 h-9" data-testid={`${testId}-month`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.map(([v, l]) => (
              <SelectItem key={v} value={v}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {filters.period === "personalizado" && (
        <>
          <Input
            type="date"
            value={filters.startDate}
            onChange={(e) => set({ startDate: e.target.value })}
            className="w-40 h-9"
            aria-label="Data inicial"
            data-testid={`${testId}-start-date`}
          />
          <Input
            type="date"
            value={filters.endDate}
            onChange={(e) => set({ endDate: e.target.value })}
            className="w-40 h-9"
            aria-label="Data final"
            data-testid={`${testId}-end-date`}
          />
        </>
      )}

      <Select value={filters.hasOccurrence} onValueChange={(v) => set({ hasOccurrence: v })}>
        <SelectTrigger className="w-40 h-9" data-testid={`${testId}-occurrence`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="todos">Todos</SelectItem>
          <SelectItem value="sim">Com ocorrência</SelectItem>
          <SelectItem value="nao">Sem ocorrência</SelectItem>
        </SelectContent>
      </Select>

      {showDeletedFilter && (
        <Select
          value={filters.includeDeleted || "ativos"}
          onValueChange={(v) => set({ includeDeleted: v })}
        >
          <SelectTrigger className="w-36 h-9" data-testid={`${testId}-deleted`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ativos">Ativos</SelectItem>
            <SelectItem value="excluidos">Excluídos</SelectItem>
            <SelectItem value="todos">Todos</SelectItem>
          </SelectContent>
        </Select>
      )}

      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={filters.search}
          onChange={(e) => set({ search: e.target.value })}
          placeholder="Buscar..."
          className="w-48 h-9 pl-7"
          aria-label="Buscar no histórico"
          data-testid={`${testId}-search`}
        />
      </div>

      {extraSelects}

      <Button
        variant="ghost"
        size="sm"
        onClick={clear}
        disabled={isDefault}
        data-testid={`${testId}-clear`}
        title="Limpar filtros"
      >
        <X size={14} className="mr-1" />
        Limpar filtros
      </Button>

      <span className="text-xs text-muted-foreground ml-1" data-testid={`${testId}-period-label`}>
        {periodLabel(filters)}
      </span>
    </div>
  );
}